import { Box, Stack } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useLangContext } from "../../../context/langContext";

import { Link, useLocation } from "react-router-dom";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import { dateFormatter } from "../../../utils/date-formatter";
import CustomTable from "../../common/tables/Table";

import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";

import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";

import {
  UsersListColumnSortable,
  UsersListRequest,
} from "../../../services/Endpoints/users/UsersListEndpoint";
import EditAction from "../../common/edit-action/EditAction";
import UserEditModal from "../user-edit-modal/UserEditModal";
import {
  DocumentText,
  Folder2,
  Messages2,
  Microphone,
  Receipt2,
} from "iconsax-react";
import { useUrl } from "../../../hooks/urlEncoding";
import { routerPaths } from "../../../constants/Paths";
import {
  TicketListColumnFilterable,
  TicketListColumnSortable,
} from "../../../services/Endpoints/tickets/TicketListEndpoints";
import {
  PaymentAdListColumnFilterable,
  PaymentAdListColumnSortable,
} from "../../../services/Endpoints/payments/PaymentAdListEndpoint";
import {
  BroadcastingListColumnFilterable,
  BroadcastingListColumnSortable,
} from "../../../services/Endpoints/broadcast";
import LinkWithPopover from "../common/LinkWithPopover";
import {
  FileListColumnFilterable,
  FileListColumnSortable,
} from "../../../services/Endpoints/files/FileListEndpoints";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import { ITableSchema } from "../../../utils/types";
import useTableSchema from "../../../hooks/useTableSchema";
import TextFilter from "../../common/tables/filters/TextFilter";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import {
  LogListColumnFilterable,
  LogListColumnSortable,
} from "../../../services/Endpoints/logs/LogListEndpoints";
import useUrlSort from "../../../hooks/useUrlSort";

export interface IUsers {
  created_at: string;
  id: number;
  mobile: string;
  name: string | null;
  email: string | null;
  mobile_verified_at: string;
  operations: ReactNode | string;
}

export interface ISupportTabsComponent {
  walletReload: boolean;
  setWalletReload: (payload: boolean) => void;
}

export default function UsersTableComponent({
  walletReload,
  setWalletReload,
}: Readonly<ISupportTabsComponent>) {
  const { queryStringUrl, parseQueryString, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [_, setValue] = useState("1");
  const { lang } = useLangContext();
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [dataTransaction, setDataTransaction] = useState<IUsers[] | []>([]);
  const [openEditModal, setOpenEditModal] = useState<
    Omit<IUsers, "operations"> | false
  >(false);
  const [total, setTotal] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const { can } = useCanAccess();
  const location = useLocation();
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();

  const getDataTransaction = async () => {
    try {
      setWaiting(true);
      const walletListRequest = new UsersListRequest(protectedInstance);
      const resp = await walletListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const transactions = resp.data.users.map(
        ({ created_at, email, mobile, name, mobile_verified_at, id }) => ({
          id,
          mobile,
          name,
          email,
          mobile_verified_at,
          created_at,
          operations: (
            <Stack
              justifyContent="center"
              gap="3px"
              alignItems="center"
              direction="row"
            >
              <EditAction
                handleEdit={() =>
                  handleView({
                    email,
                    mobile,
                    created_at,
                    id,
                    mobile_verified_at,
                    name,
                  })
                }
              />
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminTicketsList)}
                title="تیکت ها"
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.SUPPORT, {
                    filters: {
                      [TicketListColumnFilterable.CREATOR_USER_ID]: {
                        column: TicketListColumnFilterable.CREATOR_USER_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [TicketListColumnSortable.ID]: {
                        column: TicketListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <Messages2 size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminPaymentReceiptsList)}
                title="رسیدها"
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.RECEIPTS, {
                    filters: {
                      [PaymentAdListColumnFilterable.USER_ID]: {
                        column: PaymentAdListColumnFilterable.USER_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [PaymentAdListColumnSortable.ID]: {
                        column: PaymentAdListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <Receipt2 size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                title="پیام صوتی"
                disabled={!can(AccessEnums.AdminVoiceBroadcastsList)}
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.VOICE_BROADCASTS, {
                    filters: {
                      [BroadcastingListColumnFilterable.USER_ID]: {
                        column: BroadcastingListColumnFilterable.USER_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [BroadcastingListColumnSortable.ID]: {
                        column: BroadcastingListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <Microphone size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                title="فایل ها"
                disabled={!can(AccessEnums.AdminFilesList)}
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.MY_FILES, {
                    filters: {
                      [FileListColumnFilterable.USER_ID]: {
                        column: FileListColumnFilterable.USER_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [FileListColumnSortable.ID]: {
                        column: FileListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <Folder2 size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                title="لاگ ها"
                disabled={!can(AccessEnums.AdminLogsList)}
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.LOGS, {
                    filters: {
                      [LogListColumnFilterable.USER_ID]: {
                        column: LogListColumnFilterable.USER_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [LogListColumnSortable.ID]: {
                        column: LogListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <DocumentText size="20" />
                </Stack>
              </LinkWithPopover>
            </Stack>
          ),
        })
      );
      setDataTransaction(transactions);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataTransaction();
  }, [walletReload, page, rowsPerPage, location.search]);

  const handleView = (user: Omit<IUsers, "operations">) => {
    console.log("View ticket", user);
    setOpenEditModal(user);
    // navigate(`/${routerPaths.SUPPORT}/${ticketNumber}`);
  };

  const userColumns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "mobile",
      label: "شماره تماس",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "name",
      label: "نام",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "email",
      label: "ایمیل",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "created_at",
      label: "تاریخ ایجاد",
      minWidth: 100,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) => value && dateFormatter({ lang, date: value }),
    },
    {
      key: "mobile_verified_at",
      label: "تاریخ وریفای ",
      minWidth: 100,
      FilterComponent: DateTimeFilter,
      align: "center",
      format: (value: string) => value && dateFormatter({ lang, date: value }),
    },
    {
      key: "operations",
      label: "عملیات",
      minWidth: 170,
      align: "center",
    },
  ];

  const { filtersSchema, columns } = useTableSchema(userColumns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <Box
        sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}
      ></Box>

      <CustomTable
        reloadAction={() => setWalletReload(!walletReload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={dataTransaction}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
      <UserEditModal
        onCloseModal={() => setOpenEditModal(false)}
        openModal={openEditModal}
        userInfo={openEditModal != false ? openEditModal : null}
      />
    </Box>
  );
}
